import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CATEGORY_META } from "../../../mocks/allVenues";

interface Props {
  slug: string;
  totalCount: number;
}

export default function CategoryHeader({ slug, totalCount }: Props) {
  const { t } = useTranslation();
  const meta = CATEGORY_META[slug] ?? CATEGORY_META["featured"];

  return (
    <div className="relative">
      {/* Hero image */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img
          src={meta.image}
          alt={meta.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-anthracite-900/60 via-anthracite-900/40 to-anthracite-900/80" />
        {/* Breadcrumb */}
        <div className="absolute top-0 left-0 right-0 pt-20 px-4 md:px-8">
          <nav className="max-w-7xl mx-auto flex items-center gap-2 text-xs text-white/60 font-body">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">
              {t("cat_page_breadcrumb_home")}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">
              {t("cat_page_breadcrumb_cat")}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white font-medium">{meta.title}</span>
          </nav>
        </div>
        {/* Category info */}
        <div className="absolute bottom-0 left-0 right-0 pb-6 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-forest-600 shrink-0">
                <i className={`${meta.icon} text-white text-xl`} />
              </div>
              <div>
                <h1 className="font-display font-bold text-white text-3xl md:text-4xl leading-tight">
                  {meta.title}
                </h1>
                <p className="text-white/70 text-sm font-body mt-1 max-w-xl leading-relaxed">
                  {meta.desc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm font-body text-anthracite-600">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-forest-600 text-sm" />
            </div>
            <strong className="font-semibold text-anthracite-900">{totalCount}</strong>
            {t("cat_page_results")}
          </div>
          <div className="flex items-center gap-3">
            {/* Category quick links */}
            <div className="hidden md:flex items-center gap-2">
              {["historical", "food", "entertainment", "accommodation", "nature", "sightseeing"].map((s) => (
                <Link
                  key={s}
                  to={`/category/${s}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium font-body transition-colors cursor-pointer whitespace-nowrap ${
                    s === slug
                      ? "bg-forest-600 text-white"
                      : "text-anthracite-500 hover:text-anthracite-800 hover:bg-stone-100"
                  }`}
                >
                  {CATEGORY_META[s]?.title ?? s}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
